import React, { useState, useEffect } from "react";
import { Row, Card, CardBody, Button, Container } from "reactstrap";
import axios from "axios";import createSocket from "../../helpers/createSocket";
import {
  buildStoredAuthPayload,
  createAuthHandoffUrl,
} from "../../helpers/AuthHandoff";
import { END_POINT } from "../../constants/defaultValues";
import { Colxx } from "../../components/common/CustomBootstrap";
import {
  NotificatioErrController,
  NotificatioSucController,
  NotificatioSucSticky,
  AddCommaNumber,
  getQrPayWithCashAndInfo,
} from "../../helpers/Utils";

// Price mapping for display
const priceMapping = {
  basic: {
    "1m": { label: "1 tháng", price: 99000 },
    "6m": { label: "6 tháng", price: 594000 },
    "1y": { label: "1 năm", price: 1188000 },
    "2y": { label: "2 năm", price: 2376000 },
    "5y": { label: "5 năm", price: 5940000 },
  },
  pro: {
    "1m": { label: "1 tháng", price: 299000 },
    "6m": { label: "6 tháng", price: 1794000 },
    "1y": { label: "1 năm", price: 3588000 },
    "2y": { label: "2 năm", price: 7176000 },
    "5y": { label: "5 năm", price: 17940000 },
  },
  pro_auto: {
    "1m": { label: "1 tháng", price: 599000 },
    "6m": { label: "6 tháng", price: 3594000 },
    "1y": { label: "1 năm", price: 7188000 },
    "2y": { label: "2 năm", price: 14376000 },
    "5y": { label: "5 năm", price: 35940000 },
  },
};

const packageNames = {
  basic: "Gói Cơ bản",
  pro: "Gói Pro",
  pro_auto: "Gói Pro + Automation",
};

const Payment = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRequesting, setIsRequesting] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState(null);
  const [paymentCreated, setPaymentCreated] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // User data from registration
  const companyUUID = localStorage.getItem("companyUUID");
  const accessToken = localStorage.getItem("access_token");
  const userId = localStorage.getItem("user_id");
  const domain = localStorage.getItem("username");

  // MBBank payment info
  const bin = 970422;
  const account = "0935777736";
  const accountName = "Phạm Quang Thành";
  const bankName = "MBBank";

  useEffect(() => {
    // Read pending upgrade from localStorage
    const savedUpgrade = localStorage.getItem("pendingUpgrade");
    if (savedUpgrade) {
      try {
        const upgradeInfo = JSON.parse(savedUpgrade);
        setPendingUpgrade(upgradeInfo);
      } catch (e) {
        console.error("Error parsing pendingUpgrade:", e);
      }
    }

    // Check if user has required auth data
    if (!companyUUID || !accessToken) {
      NotificatioErrController(
        "Không tìm thấy thông tin tài khoản. Vui lòng đăng ký lại.",
      );
      setTimeout(() => {
        window.location.href = "/user/register";
      }, 2000);
      return;
    }

    setIsLoading(false);
  }, []);

  // Initialize Socket.IO for real-time payment updates
  useEffect(() => {
    if (!userId) return;

    const newSocket = createSocket();
    newSocket.emit("join room", { room_id: userId });

    // Listen for payment received event
    newSocket.on("upgrade-payment-received", (data) => {
      console.log("Payment received:", data);
      NotificatioSucController(
        `Đã nhận thanh toán ${data.amount.toLocaleString("vi-VN")}đ từ ${data.gateway}`,
      );
    });

    // Listen for upgrade completed event
    newSocket.on("upgrade-completed", (data) => {
      console.log("Upgrade completed:", data);
      setPaymentSuccess(true);
      NotificatioSucSticky(
        "Nâng cấp tài khoản thành công! Đang chuyển hướng...",
      );

      // Clear pending upgrade from localStorage
      localStorage.removeItem("pendingUpgrade");

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        redirectToDashboard();
      }, 3000);
    });

    // Listen for payment pending review
    newSocket.on("upgrade-payment-pending-review", (data) => {
      console.log("Payment pending review:", data);
      NotificatioSucController(data.message);
    });

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [userId]);

  // Create upgrade requirement when component is ready
  useEffect(() => {
    if (!pendingUpgrade || !accessToken || paymentCreated) return;

    createUpgradeRequirement();
  }, [pendingUpgrade, accessToken, paymentCreated]);

  const createUpgradeRequirement = async () => {
    if (!pendingUpgrade) return;

    const priceInfo =
      priceMapping[pendingUpgrade.package]?.[pendingUpgrade.duration];
    if (!priceInfo) {
      NotificatioErrController("Gói cước không hợp lệ");
      return;
    }

    setIsRequesting(true);

    try {
      const response = await axios.post(
        `${END_POINT}/user-license/requirement`,
        {
          time: pendingUpgrade.duration,
          purchased_package: pendingUpgrade.package,
          price: priceInfo.price,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.data) {
        setPaymentCreated(true);
        NotificatioSucController(
          "Đã tạo yêu cầu nâng cấp. Vui lòng thanh toán để kích hoạt.",
        );
      }
    } catch (error) {
      console.error("Error creating upgrade requirement:", error);
      NotificatioErrController(
        error.response?.data?.responseText ||
          "Có lỗi xảy ra khi tạo yêu cầu nâng cấp",
      );
    } finally {
      setIsRequesting(false);
    }
  };

  const redirectToDashboard = async () => {
    const protocol = window.location.protocol;
    const baseUrl = window.location.host.replace("register.", "");
    const dashboardUrl = `${protocol}//${domain}.${baseUrl}/app/dashboards/default`;

    try {
      window.location.href = await createAuthHandoffUrl(
        dashboardUrl,
        buildStoredAuthPayload(),
      );
    } catch (error) {
      console.error("Error creating auth handoff:", error);
      NotificatioErrController("Không thể chuyển phiên đăng nhập");
    }
  };

  const handleSkipPayment = async () => {
    try {
      // Call API to cancel pending upgrade and set to free package
      await axios.post(
        `${END_POINT}/user-license/cancel-pending`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      NotificatioSucController("Đã chuyển về gói miễn phí");
    } catch (error) {
      console.error("Error cancelling pending upgrade:", error);
      // Still redirect even if API fails - the user wants to skip
    }

    // Clear pending upgrade from localStorage and go to dashboard
    localStorage.removeItem("pendingUpgrade");
    redirectToDashboard();
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="loading" />
      </div>
    );
  }

  if (!pendingUpgrade) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Colxx xxs="12" md="8" lg="6">
            <Card className="shadow">
              <CardBody className="text-center py-5">
                <i
                  className="simple-icon-info text-warning"
                  style={{ fontSize: "4rem" }}
                />
                <h4 className="mt-4">Không có thông tin thanh toán</h4>
                <p className="text-muted mb-4">
                  Bạn chưa chọn gói cước nào để nâng cấp
                </p>
                <Button color="primary" onClick={redirectToDashboard}>
                  Vào trang quản lý
                </Button>
              </CardBody>
            </Card>
          </Colxx>
        </Row>
      </Container>
    );
  }

  const priceInfo =
    priceMapping[pendingUpgrade.package]?.[pendingUpgrade.duration];
  const price = priceInfo?.price || 0;

  if (paymentSuccess) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Colxx xxs="12" md="8" lg="6">
            <Card className="shadow">
              <CardBody className="text-center py-5">
                <i
                  className="simple-icon-check text-success"
                  style={{ fontSize: "5rem" }}
                />
                <h3 className="mt-4 text-success">Thanh toán thành công!</h3>
                <p className="text-muted mb-4">
                  Tài khoản của bạn đã được nâng cấp. Đang chuyển hướng...
                </p>
                <div className="loading" style={{ width: 30, height: 30 }} />
              </CardBody>
            </Card>
          </Colxx>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Colxx xxs="12" md="10" lg="8">
          <Card className="shadow">
            <CardBody className="p-4 p-md-5">
              {/* Header */}
              <div className="text-center mb-4">
                <a href="https://vmass.vn" className="d-inline-block mb-3">
                  <span className="logo-single" />
                </a>
                <h3 className="font-weight-bold">Thanh toán nâng cấp</h3>
                <p className="text-muted">
                  Quét mã QR để thanh toán. Hệ thống sẽ tự động kích hoạt gói
                  cước ngay sau khi nhận được thanh toán.
                </p>
              </div>

              {/* Package Info */}
              <div
                className="bg-light p-4 mb-4"
                style={{ border: "2px solid #e9ecef" }}
              >
                <Row>
                  <Colxx xxs="6">
                    <p className="text-muted mb-1">Gói cước:</p>
                    <h5 className="font-weight-bold text-primary mb-0">
                      {packageNames[pendingUpgrade.package] ||
                        pendingUpgrade.package}
                    </h5>
                  </Colxx>
                  <Colxx xxs="6" className="text-right">
                    <p className="text-muted mb-1">Thời hạn:</p>
                    <h5 className="font-weight-bold mb-0">
                      {priceInfo?.label || pendingUpgrade.duration}
                    </h5>
                  </Colxx>
                </Row>
                <hr />
                <Row>
                  <Colxx xxs="6">
                    <p className="text-muted mb-0">Số tiền thanh toán:</p>
                  </Colxx>
                  <Colxx xxs="6" className="text-right">
                    <h4 className="text-danger font-weight-bold mb-0">
                      {AddCommaNumber(price)} đ
                    </h4>
                  </Colxx>
                </Row>
              </div>

              {/* QR Code and Bank Info */}
              <Row className="align-items-center">
                <Colxx xxs="12" md="5" className="text-center mb-4 mb-md-0">
                  <p className="text-muted small mb-2">
                    Quét mã QR để thanh toán
                  </p>
                  <img
                    src={getQrPayWithCashAndInfo(
                      bin,
                      account,
                      price,
                      "VMASS" + companyUUID,
                    )}
                    alt="QR Payment"
                    className="img-fluid shadow"
                    style={{ maxWidth: "220px" }}
                  />
                </Colxx>
                <Colxx xxs="12" md="7">
                  <div className="pl-md-4">
                    <h6 className="font-weight-bold mb-3">
                      Hoặc chuyển khoản thủ công:
                    </h6>
                    <table className="table table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted py-1">Ngân hàng:</td>
                          <td className="font-weight-bold py-1">{bankName}</td>
                        </tr>
                        <tr>
                          <td className="text-muted py-1">Số tài khoản:</td>
                          <td className="font-weight-bold py-1">{account}</td>
                        </tr>
                        <tr>
                          <td className="text-muted py-1">Chủ tài khoản:</td>
                          <td className="font-weight-bold py-1">
                            {accountName}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted py-1">Số tiền:</td>
                          <td className="font-weight-bold text-danger py-1">
                            {AddCommaNumber(price)} đ
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted py-1">Nội dung:</td>
                          <td className="font-weight-bold text-primary py-1">
                            VMASS{companyUUID}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Colxx>
              </Row>

              {/* Status indicator */}
              <div className="text-center mt-4 pt-3 border-top">
                {isRequesting ? (
                  <div className="text-info d-flex align-items-center justify-content-center">
                    <div
                      className="loading"
                      style={{ width: 20, height: 20 }}
                    />
                    <span className="ml-2">Đang tạo yêu cầu nâng cấp...</span>
                  </div>
                ) : (
                  <div className="text-muted">
                    <i className="simple-icon-clock mr-2" />
                    <span>
                      Đang chờ thanh toán... Hệ thống sẽ tự động kích hoạt khi
                      nhận được tiền.
                    </span>
                  </div>
                )}
              </div>

              {/* Skip button */}
              <div className="text-center mt-4">
                <Button
                  color="link"
                  className="text-muted"
                  onClick={handleSkipPayment}
                >
                  Bỏ qua, sử dụng gói miễn phí →
                </Button>
              </div>
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    </Container>
  );
};

export default Payment;
